import { UseParkingQueueReturn } from '../../hooks/parkingQueue';
import { QueueRowFacts } from '../../Helpers/parkingQueueUtils';
import { QueueLogEvent, QUEUE_CLOSE_REASON_LABELS, QUEUE_WISH_LABELS, QueueCloseReason } from '../../types/parkingQueue';
import { ParkingSpotType } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';

type Labels = Pick<UseParkingQueueReturn, 'buildingName' | 'areaLabel'>;

/** "Byggnad A: Garage, Gård · Byggnad B: Carport" */
export const wantedAreasText = (wantedAreas: { [areaKey: string]: string }, labels: Labels): string => {
    const perBuilding: { [buildingKey: string]: string[] } = {};
    Object.entries(wantedAreas).forEach(([areaKey, buildingKey]) => {
        (perBuilding[buildingKey] ??= []).push(labels.areaLabel(buildingKey, areaKey));
    });
    return Object.entries(perBuilding)
        .map(([b, areas]) => `${labels.buildingName(b)}: ${areas.sort((x, y) => x.localeCompare(y, 'sv')).join(', ')}`)
        .join(' · ');
};

/** Spot types, charger and outlet wishes as short Swedish phrases; empty when nothing is set. */
export const wishesText = (row: QueueRowFacts): string[] => {
    const { entry } = row;
    const parts: string[] = [];
    const types = Object.keys(entry.spotTypes ?? {}) as ParkingSpotType[];
    if (types.length > 0) parts.push(types.map(t => translate(t)).join('/'));
    if (entry.wantsCharger) parts.push(`Laddplats: ${QUEUE_WISH_LABELS[entry.wantsCharger]}`);
    if (entry.wantsOutlet) parts.push(`Motorvärmare: ${QUEUE_WISH_LABELS[entry.wantsOutlet]}`);
    return parts;
};

const flipJaNej = (value?: string): string => value === 'ja' ? 'nej' : value === 'nej' ? 'ja' : (value ?? '–');

/** Swedish description of a log event built from type/from/to. */
export const describeLogEvent = (event: QueueLogEvent): string => {
    const change = (what: string) => `${what} ändrad${event.from || event.to ? `: ${event.from ?? '–'} → ${event.to ?? '–'}` : ''}`;
    switch (event.type) {
        case 'created': return 'Köplats skapad';
        case 'queueDateChanged': return change('Ködatum');
        // Stored as intern ja/nej; shown the way the form asks it.
        case 'internalChanged': return `Räknas som extern ändrad: ${flipJaNej(event.from)} → ${flipJaNej(event.to)}`;
        case 'areasChanged': return 'Önskade områden ändrade';
        case 'spotTypesChanged': return 'Platstyper ändrade';
        case 'chargerChanged': return change('Laddplats');
        case 'outletChanged': return change('Motorvärmare');
        case 'commentChanged': return 'Kommentar ändrad';
        case 'closed': return `Avslutad${event.to ? ` (${QUEUE_CLOSE_REASON_LABELS[event.to as QueueCloseReason] ?? event.to})` : ''}`;
        case 'reopened': return 'Återöppnad';
        case 'deviation': return `Avvikelse från standardordningen${event.to ? ` (${event.to} sökande före)` : ''}`;
        case 'leaseLinked': return 'Hyresavtal kopplat';
        case 'note': return 'Anteckning';
        default: return event.type;
    }
};

/** "Intern · Lägenhet 1201" (from the lease), "Intern" (marked by hand) or "Extern"; with buildingName, "Intern · Eken 2 · Lägenhet 1201". */
export const internalText = (row: Pick<QueueRowFacts, 'residence' | 'entry'>, buildingName?: (buildingKey: string) => string): string => {
    if (row.residence) {
        return `Intern · ${buildingName ? `${buildingName(row.residence.buildingKey)} · ` : ''}${row.residence.label}`;
    }
    return row.entry.markedInternal ? 'Intern' : 'Extern';
};

export const PII_HINT = 'Anteckningar om sökande är personuppgifter – skriv sakligt.';
